// Keep all episodes readable as a grid until the desktop carousel is ready.
if (!customElements.get('sa-podcast-carousel')) {
  customElements.define('sa-podcast-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.frame = requestAnimationFrame(() => this.setup());
    }

    setup() {
      this.viewport = this.querySelector('[data-podcast-viewport]');
      this.track = this.querySelector('[data-podcast-track]');
      this.controls = this.querySelector('[data-podcast-controls]');
      this.status = this.querySelector('[data-podcast-status]');
      if (!this.viewport || !this.track || !this.controls) return;
      this.cards = [...this.track.children];
      this.desktop = matchMedia('(min-width: 1000px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      const signal = this.controller.signal;
      this.desktop.addEventListener('change', () => this.updateLayout(), { signal });
      this.reducedMotion.addEventListener('change', () => {
        this.carousel?.reInit({ duration: this.reducedMotion.matches ? 0 : 25 });
      }, { signal });
      this.controls.addEventListener('click', (event) => {
        if (event.target.closest('[data-podcast-previous]')) this.move(-1);
        if (event.target.closest('[data-podcast-next]')) this.move(1);
      }, { signal });
      this.viewport.addEventListener('keydown', (event) => {
        if (!this.carousel || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      this.viewport.addEventListener('wheel', (event) => this.onWheel(event), { passive: false, signal });
      // A script added by the theme editor can finish loading after the section.
      document.querySelectorAll('script[src*="embla-carousel.umd.js"]').forEach((script) => {
        script.addEventListener('load', () => this.updateLayout(), { once: true, signal });
      });
      window.addEventListener('load', () => this.updateLayout(), { once: true, signal });
      this.updateLayout();
    }

    updateLayout() {
      if (this.cards.length <= 3) {
        this.destroyCarousel();
        return;
      }
      if (!this.desktop.matches) {
        this.destroyCarousel();
        this.showMobilePage(this.mobilePage || 0);
        return;
      }
      if (this.classList.contains('is-mobile-pagination')) this.destroyCarousel();
      if (this.carousel || typeof window.EmblaCarousel !== 'function') return;
      this.classList.add('is-carousel');
      this.setAttribute('role', 'region');
      this.setAttribute('aria-roledescription', 'carousel');
      this.viewport.tabIndex = 0;
      this.viewport.setAttribute('aria-label', 'Podcast episodes. Use the left and right arrow keys to browse.');
      this.cards.forEach((card, index) => {
        card.setAttribute('role', 'group');
        card.setAttribute('aria-roledescription', 'slide');
        card.setAttribute('aria-label', `${index + 1} of ${this.cards.length}`);
      });
      this.carousel = window.EmblaCarousel(this.viewport, {
        align: 'start',
        loop: true,
        slidesToScroll: 1,
        inViewThreshold: 0.01,
        duration: this.reducedMotion.matches ? 0 : 25
      });
      this.controls.hidden = false;
      this.carousel.on('pointerDown', () => this.classList.add('is-dragging'));
      this.carousel.on('pointerUp', () => this.classList.remove('is-dragging'));
      this.carousel.on('slidesInView', () => this.updateVisibleCards());
      this.carousel.on('reInit', () => this.updateVisibleCards());
      this.carousel.on('settle', () => {
        this.updateVisibleCards();
        if (this.status) this.status.textContent = `Episode ${this.carousel.selectedScrollSnap() + 1} of ${this.cards.length}`;
      });
      this.updateVisibleCards();
    }

    updateVisibleCards() {
      if (!this.carousel) return;
      const visible = new Set(this.carousel.slidesInView());
      // The initial IntersectionObserver result can arrive after initialization.
      if (!visible.size) return;
      this.cards.forEach((card, index) => {
        const hidden = !visible.has(index);
        card.inert = hidden;
        if (hidden) card.setAttribute('aria-hidden', 'true');
        else card.removeAttribute('aria-hidden');
      });
    }

    move(direction) {
      if (!this.desktop.matches && this.cards.length > 3) {
        this.showMobilePage((this.mobilePage || 0) + direction);
        this.viewport.scrollIntoView({ block: 'start', behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
        return;
      }
      if (!this.carousel) return;
      if (direction > 0) this.carousel.scrollNext(this.reducedMotion.matches);
      else this.carousel.scrollPrev(this.reducedMotion.matches);
    }

    showMobilePage(page) {
      const pageCount = Math.ceil(this.cards.length / 3);
      this.mobilePage = (page + pageCount) % pageCount;
      const start = this.mobilePage * 3;
      this.cards.forEach((card, index) => { card.hidden = index < start || index >= start + 3; });
      this.classList.add('is-mobile-pagination');
      this.controls.hidden = false;
      this.controls.setAttribute('aria-label', 'Podcast episode groups');
      this.controls.querySelector('[data-podcast-previous]').setAttribute('aria-label', 'Previous podcast episodes');
      this.controls.querySelector('[data-podcast-next]').setAttribute('aria-label', 'Next podcast episodes');
      if (this.status) this.status.textContent = `Episodes ${start + 1}–${Math.min(start + 3, this.cards.length)} of ${this.cards.length}`;
    }

    onWheel(event) {
      if (!this.carousel || event.ctrlKey) return;
      // Preserve normal vertical page scrolling and pinch-to-zoom.
      const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY);
      if (!horizontal && !event.shiftKey) return;
      const delta = horizontal ? event.deltaX : event.deltaY;
      if (!delta) return;
      event.preventDefault();
      const now = performance.now();
      if (now - (this.lastWheelTime || 0) > 160 || Math.sign(delta) !== Math.sign(this.wheelDistance || 0)) this.wheelDistance = 0;
      this.lastWheelTime = now;
      const scale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.viewport.clientWidth : 1;
      this.wheelDistance = (this.wheelDistance || 0) + delta * scale;
      if (Math.abs(this.wheelDistance) >= 35 && now - (this.lastWheelMove || 0) > 220) {
        this.move(Math.sign(this.wheelDistance));
        this.wheelDistance = 0;
        this.lastWheelMove = now;
      }
    }

    destroyCarousel() {
      this.carousel?.destroy();
      this.carousel = null;
      this.classList.remove('is-carousel', 'is-dragging', 'is-mobile-pagination');
      this.removeAttribute('role');
      this.removeAttribute('aria-roledescription');
      this.viewport?.removeAttribute('tabindex');
      this.viewport?.removeAttribute('aria-label');
      if (this.controls) {
        this.controls.hidden = true;
        this.controls.setAttribute('aria-label', 'Podcast carousel controls');
        this.controls.querySelector('[data-podcast-previous]').setAttribute('aria-label', 'Previous podcast episode');
        this.controls.querySelector('[data-podcast-next]').setAttribute('aria-label', 'Next podcast episode');
      }
      if (this.status) this.status.textContent = '';
      this.cards?.forEach((card) => {
        card.hidden = false;
        card.inert = false;
        ['role', 'aria-roledescription', 'aria-label', 'aria-hidden'].forEach((attribute) => card.removeAttribute(attribute));
      });
      this.wheelDistance = 0;
      this.lastWheelTime = 0;
      this.lastWheelMove = 0;
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.controller?.abort();
      this.controller = null;
      this.destroyCarousel();
    }
  });
}
